import { ShieldCheckIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import {
  Input,
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  RadioGroup,
  Radio,
} from '@nextui-org/react';
import React, { useState, useEffect } from 'react';

import { ServerUUID, ServerName, ServerHost, ClusterTag } from '@common/types';
import { ModernModal } from '@portal/components/common/ModernModal.ui';
import { PasswordInput } from '@portal/components/common/PasswordInput.ui';
import { TypographyUI } from '@portal/components/common/Typography.ui';

type PasswordMode = 'single' | 'perServer';

interface SelectiveBatchSslModalProps {
  isOpen: boolean;
  onOpenChange: () => void;
  clusterId: string;
  availableServers: Array<{
    uuid: ServerUUID;
    name: ServerName;
    host: ServerHost;
    clusterTag: ClusterTag;
  }>;
  totalServers: number;
  sslEnabledCount: number;
  username: string;
  password: string;
  onUsernameChange: (val: string) => void;
  onPasswordChange: (val: string) => void;
  onConfirm: (
    mode: PasswordMode,
    selectedServerUUIDs: ServerUUID[],
    serverPasswords?: Array<{ serverUUID: ServerUUID; password: string }>
  ) => void;
  isLoading?: boolean;
}

export const SelectiveBatchSslModal: React.FC<SelectiveBatchSslModalProps> = ({
  isOpen,
  onOpenChange,
  clusterId,
  availableServers,
  totalServers,
  sslEnabledCount,
  username,
  password,
  onUsernameChange,
  onPasswordChange,
  onConfirm,
  isLoading,
}) => {
  const [selectedServers, setSelectedServers] = useState<Set<ServerUUID>>(new Set());
  const [passwordMode, setPasswordMode] = useState<PasswordMode>('single');
  const [serverPasswords, setServerPasswords] = useState<Record<ServerUUID, string>>({});
  const [uploadError, setUploadError] = useState<string>('');

  // Reset selections when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedServers(new Set());
      setPasswordMode('single');
      setServerPasswords({});
    }
  }, [isOpen]);

  const handleSelectAll = () => {
    if (selectedServers.size === availableServers.length) {
      setSelectedServers(new Set());
      if (passwordMode === 'perServer') {
        setServerPasswords({});
      }
    } else {
      const allUUIDs = availableServers.map(s => s.uuid);
      setSelectedServers(new Set(allUUIDs));
      if (passwordMode === 'perServer') {
        const newPasswords: Record<ServerUUID, string> = {};
        allUUIDs.forEach(uuid => {
          newPasswords[uuid] = serverPasswords[uuid] || '';
        });
        setServerPasswords(newPasswords);
      }
    }
  };

  const handleToggleServer = (serverUUID: ServerUUID) => {
    const newSelected = new Set(selectedServers);
    if (newSelected.has(serverUUID)) {
      newSelected.delete(serverUUID);
      if (passwordMode === 'perServer') {
        const newPasswords = { ...serverPasswords };
        delete newPasswords[serverUUID];
        setServerPasswords(newPasswords);
      }
    } else {
      newSelected.add(serverUUID);
      if (passwordMode === 'perServer') {
        setServerPasswords({ ...serverPasswords, [serverUUID]: '' });
      }
    }
    setSelectedServers(newSelected);
  };

  const handlePasswordChange = (serverUUID: ServerUUID, password: string) => {
    setServerPasswords({ ...serverPasswords, [serverUUID]: password });
    setUploadError(''); // Clear upload error when user manually changes password
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      // Reset file input if no file selected
      event.target.value = '';
      return;
    }

    // Store the file input reference to reset it later
    const fileInput = event.target;

    // Only allow text files
    if (!file.type.includes('text') && !file.name.endsWith('.txt')) {
      setUploadError('Please upload a .txt file');
      // Reset file input so user can re-select the same file
      fileInput.value = '';
      return;
    }

    try {
      const text = await file.text();
      const passwords = text
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0); // Remove empty lines

      const selectedCount = selectedServers.size;
      if (passwords.length < selectedCount) {
        setUploadError(
          `Not enough passwords in file. Found ${passwords.length} password(s), but ${selectedCount} server(s) selected. Please provide at least ${selectedCount} password(s).`
        );
        // Reset file input so user can re-select the same file
        fileInput.value = '';
        return;
      }

      // Assign passwords to selected servers in order
      const selectedServerArray = Array.from(selectedServers);
      const newPasswords: Record<ServerUUID, string> = { ...serverPasswords };

      selectedServerArray.forEach((serverUUID, index) => {
        if (index < passwords.length) {
          newPasswords[serverUUID] = passwords[index];
        }
      });

      setServerPasswords(newPasswords);
      setUploadError(''); // Clear any previous errors

      // Reset file input after successful upload
      fileInput.value = '';
    } catch (error) {
      setUploadError('Failed to read file. Please make sure it is a valid text file.');
      console.error('Error reading file:', error);
      // Reset file input so user can re-select the same file
      fileInput.value = '';
    }
  };

  const handleConfirm = () => {
    if (passwordMode === 'single') {
      onConfirm('single', Array.from(selectedServers));
    } else {
      const serverPasswordsArray = Array.from(selectedServers).map(uuid => ({
        serverUUID: uuid,
        password: serverPasswords[uuid] || '',
      }));
      onConfirm('perServer', Array.from(selectedServers), serverPasswordsArray);
    }
  };

  const icon = <ShieldCheckIcon className='h-4 w-4' />;
  const subtitle = `Cluster: ${clusterId}`;
  const selectedCount = selectedServers.size;
  const allSelected = selectedServers.size === availableServers.length && availableServers.length > 0;
  const someSelected = selectedServers.size > 0 && selectedServers.size < availableServers.length;

  // Check if all selected servers have passwords filled (for per-server mode)
  const allPasswordsFilled =
    passwordMode === 'single' ||
    Array.from(selectedServers).every(uuid => {
      const pwd = serverPasswords[uuid];
      return pwd && pwd.trim().length > 0;
    });

  const isFormValid = username && selectedCount > 0 && (passwordMode === 'single' ? password : allPasswordsFilled);

  const footer = (
    <>
      <Button color='danger' variant='light' onPress={onOpenChange} disabled={isLoading}>
        Cancel
      </Button>
      <Button color='primary' onPress={handleConfirm} isLoading={isLoading} isDisabled={!isFormValid}>
        Update {selectedCount} {selectedCount === 1 ? 'Server' : 'Servers'}
      </Button>
    </>
  );

  return (
    <ModernModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title='Selective Batch SSL Password Update'
      subtitle={subtitle}
      icon={icon}
      size='2xl'
      footer={footer}
    >
      <div className='flex flex-col gap-3'>
        {/* Password Mode Selection */}
        <div className='bg-gray-50 p-3 rounded-lg'>
          <TypographyUI className='text-sm font-medium text-gray-800 mb-2'>Password Mode</TypographyUI>
          <RadioGroup
            value={passwordMode}
            onValueChange={(value: string) => {
              const mode = value as PasswordMode;
              setPasswordMode(mode);
              if (mode === 'single') {
                // Clear per-server passwords when switching to single mode
                setServerPasswords({});
              } else {
                // Initialize empty passwords for selected servers when switching to per-server mode
                const newPasswords: Record<ServerUUID, string> = {};
                Array.from(selectedServers).forEach(uuid => {
                  newPasswords[uuid] = serverPasswords[uuid] || '';
                });
                setServerPasswords(newPasswords);
              }
            }}
            orientation='horizontal'
          >
            <Radio value='single'>Single Password for All</Radio>
            <Radio value='perServer'>Custom Password for Each</Radio>
          </RadioGroup>
        </div>

        {/* Summary Section */}
        <div className='bg-blue-50 p-3 rounded-lg'>
          <TypographyUI className='text-sm font-medium text-blue-800 mb-2'>Summary</TypographyUI>
          <div className='flex items-center gap-6 text-sm'>
            <div className='flex items-center gap-2'>
              <TypographyUI className='text-gray-600'>Total servers:</TypographyUI>
              <TypographyUI className='font-medium'>{totalServers}</TypographyUI>
            </div>
            <div className='flex items-center gap-2'>
              <TypographyUI className='text-gray-600'>SSL enabled:</TypographyUI>
              <TypographyUI className='font-medium text-green-600'>{sslEnabledCount}</TypographyUI>
            </div>
            <div className='flex items-center gap-2'>
              <TypographyUI className='text-gray-600'>Selected:</TypographyUI>
              <TypographyUI className='font-medium text-blue-600'>{selectedCount}</TypographyUI>
            </div>
          </div>
        </div>

        {/* Selectable Servers Table */}
        <div>
          <div className='flex items-center justify-between mb-2'>
            <TypographyUI className='text-sm font-medium'>Select servers to update:</TypographyUI>
            <div className='flex items-center gap-3'>
              {passwordMode === 'perServer' && selectedCount > 0 && (
                <label className='cursor-pointer'>
                  <input
                    type='file'
                    accept='.txt,text/plain'
                    onChange={handleFileUpload}
                    className='hidden'
                    id='password-file-upload'
                  />
                  <Button
                    as='span'
                    size='sm'
                    variant='bordered'
                    startContent={<ArrowUpTrayIcon className='h-4 w-4' />}
                    className='text-xs'
                  >
                    Upload Passwords (.txt)
                  </Button>
                </label>
              )}
              <Checkbox
                isSelected={allSelected}
                isIndeterminate={someSelected}
                onValueChange={handleSelectAll}
                size='sm'
              >
                <TypographyUI className='text-sm'>Select All</TypographyUI>
              </Checkbox>
            </div>
          </div>
          {uploadError && (
            <div className='mb-2 p-2 bg-red-50 border border-red-200 rounded-lg'>
              <TypographyUI className='text-sm text-red-800'>{uploadError}</TypographyUI>
            </div>
          )}
          <div className='max-h-96 overflow-auto'>
            <Table aria-label='Selectable servers list'>
              <TableHeader>
                <TableColumn width={50}>Select</TableColumn>
                <TableColumn>Server Name</TableColumn>
                <TableColumn>Host</TableColumn>
                <TableColumn>Cluster</TableColumn>
              </TableHeader>
              <TableBody>
                {availableServers.flatMap(server => {
                  const isSelected = selectedServers.has(server.uuid);
                  const rows = [
                    <TableRow
                      key={`${server.uuid}-main`}
                      className='cursor-pointer hover:bg-gray-50'
                      onClick={() => handleToggleServer(server.uuid)}
                    >
                      <TableCell>
                        <Checkbox
                          isSelected={isSelected}
                          onValueChange={() => handleToggleServer(server.uuid)}
                          size='sm'
                          onClick={e => e.stopPropagation()} // Prevent double toggle
                        />
                      </TableCell>
                      <TableCell>
                        <TypographyUI className='font-medium'>{server.name}</TypographyUI>
                      </TableCell>
                      <TableCell>
                        <TypographyUI className='text-gray-600'>{server.host}</TypographyUI>
                      </TableCell>
                      <TableCell>
                        <TypographyUI className='text-gray-600'>{server.clusterTag}</TypographyUI>
                      </TableCell>
                    </TableRow>,
                  ];

                  if (isSelected && passwordMode === 'perServer') {
                    rows.push(
                      <TableRow
                        key={`${server.uuid}-password`}
                        onClick={e => e.stopPropagation()} // Prevent row selection when clicking password field
                      >
                        <TableCell>{/* Empty cell for checkbox column */}</TableCell>
                        <TableCell>
                          <div className='py-2' onClick={e => e.stopPropagation()}>
                            <PasswordInput
                              label={`Password for ${server.name}`}
                              placeholder={`Enter password for ${server.name}`}
                              value={serverPasswords[server.uuid] || ''}
                              onValueChange={val => handlePasswordChange(server.uuid, val)}
                              isRequired
                              variant='bordered'
                              size='sm'
                              defaultVisible={true}
                            />
                          </div>
                        </TableCell>
                        <TableCell>{/* Empty cell for host column */}</TableCell>
                        <TableCell>{/* Empty cell for cluster column */}</TableCell>
                      </TableRow>
                    );
                  }

                  return rows;
                })}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* SSL Credentials */}
        <div className='space-y-3'>
          <TypographyUI className='text-sm font-medium'>SSL Credentials</TypographyUI>
          <Input label='SSL Username' value={username} onValueChange={onUsernameChange} isDisabled variant='bordered' />
          {passwordMode === 'single' && (
            <PasswordInput
              label='SSL Password'
              placeholder='Enter SSL password for all selected servers'
              value={password}
              onValueChange={onPasswordChange}
              isRequired
              variant='bordered'
            />
          )}
        </div>

        {/* Warning */}
        <div className='bg-yellow-50 p-3 rounded-lg'>
          <TypographyUI className='text-sm text-yellow-800'>
            ⚠️ This action will update SSL passwords for {selectedCount > 0 ? selectedCount : 'selected'} server
            {selectedCount !== 1 ? 's' : ''} in the &quot;{clusterId}&quot; cluster. This operation cannot be undone.
          </TypographyUI>
        </div>
      </div>
    </ModernModal>
  );
};
